/**
 * Service Worker registration for offline support.
 * Only registers in production builds — Workbox config will generate
 * the actual service worker file via RSBuild plugin.
 */

type UpdateCallback = () => void;

let updateCallback: UpdateCallback | null = null;

/**
 * Set a callback to be invoked when a new service worker is available.
 * Typically used to show an "Update available" toast.
 */
export function onServiceWorkerUpdate(callback: UpdateCallback): void {
  updateCallback = callback;
}

/**
 * Register the service worker.
 * Call once from index.tsx after the app renders.
 */
export function registerSW(): void {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  if (!('serviceWorker' in navigator)) {
    console.info('[SW] Service workers not supported in this browser.');
    return;
  }

  window.addEventListener('load', () => {
    void registerServiceWorker();
  });
}

async function registerServiceWorker(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    // Check for updates on registration
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New content available — notify the app
          console.info('[SW] New content available; please refresh.');
          updateCallback?.();
        }
      });
    });

    // Periodically check for updates (every 60 minutes)
    setInterval(
      () => {
        void registration.update();
      },
      60 * 60 * 1000,
    );

    console.info('[SW] Service worker registered successfully.');
  } catch (error) {
    console.error('[SW] Service worker registration failed:', error);
  }
}
